'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useRef } from 'react';

import DrupalImage from '@commons/components/Image/Drupal';
import RichText from '@commons/components/RichText';
import BaseContainer from '@commons/containers/BaseContainer';
import Flex from '@commons/containers/Flex';
import ResponsiveContainer from '@commons/containers/ResponsiveContainer';
import { usePageContext } from '@commons/contexts/page';

import type { ImageRevealProps } from '.';
import styles from './ImageReveal.module.scss';

// GSAP Plugins registration.

gsap.registerPlugin(ScrollTrigger);

// GSAP Constants.

const GSAP_SCROLL_DURATION = 400;

const GSAP_PAUSE = 0.25;

const GSAP_EASE = 'power2.out';

/**
 * ImageReveal block component.
 *
 * @see https://gsap.com/resources/React/
 * @see https://gsap.com/docs/v3/Plugins/ScrollTrigger/
 */

const ImageReveal = ({ content }: ImageRevealProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textWrapperRef = useRef<HTMLDivElement>(null);

  const { isEditing } = usePageContext();

  const { body, media, zoomLevel, theme } = content;

  const targetScale = zoomToScale(zoomLevel);

  useGSAP(() => {
    // Early return if the editor is active, on mobile, or if refs are not set.
    if (isEditing) return;
    if (!containerRef.current || !imageRef.current || !textRef.current) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: 'top top',
        end: `+=${GSAP_SCROLL_DURATION}%`,
        pin: true,
        scrub: true,
      },
    });

    tl.fromTo(
      imageRef.current,
      { scale: 1 },
      { scale: targetScale, duration: 1.25, ease: GSAP_EASE }
    )
      .to({}, { duration: GSAP_PAUSE })
      .fromTo(
        textWrapperRef.current,
        { autoAlpha: 1, yPercent: 100 },
        { autoAlpha: 1, yPercent: 0, ease: GSAP_EASE, duration: 1.25 }
      )
      .to({}, { duration: GSAP_PAUSE })
      .fromTo(textRef.current, { autoAlpha: 0 }, { autoAlpha: 1, ease: GSAP_EASE, duration: 0.5 })
      .to({}, { duration: GSAP_PAUSE });

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
    };
  }, [targetScale]);

  const transformOrigin = media.focalPoint
    ? `${media.focalPoint.relative.x} ${media.focalPoint.relative.y}`
    : 'center center';

  return (
    <BaseContainer ref={containerRef} theme={theme} className={styles.container}>
      <Flex
        ref={imageRef}
        className={styles.imageWrapper}
        style={{ transformOrigin: transformOrigin }}
      >
        <DrupalImage {...media} className={styles.image} fill />
      </Flex>
      <ResponsiveContainer
        ref={textWrapperRef}
        theme={theme}
        horizontalAlignment="center"
        verticalAlignment="center"
        className={styles.textWrapper}
      >
        <RichText ref={textRef} value={body} width={{ xs: 12, md: 10, lg: 9, xl: 8 }} />
      </ResponsiveContainer>
    </BaseContainer>
  );
};

/**
 * Parse a zoom value from Drupal into a scale factor.
 *
 * @param zoom - The zoom level as a string (e.g., "150%", "2") or number (e.g., 1.5, 2).
 *
 * @returns The corresponding scale factor (always >= 1).
 */

const zoomToScale = (input?: string | number) => {
  let scale = 1;

  if (typeof input === 'number') {
    scale = input;
  } else if (typeof input === 'string') {
    if (input.endsWith('%')) {
      scale = parseFloat(input) / 100;
    } else if (/^\d+(\.\d+)?$/.test(input)) {
      scale = parseFloat(input);
    }
  }

  // Clamp to ensure scale is always >= 1.
  return scale >= 1 ? scale : 1;
};

export default ImageReveal;
